import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useDebounce } from 'use-lodash-debounce';
import { Transition } from 'src/components/dialogPopUp';
import { Iconify } from 'src/components';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { callApi } from 'src/utils/apiCall';
import { getReportLeadContactCount } from 'src/utils/reportLead';
import { parseApiDate } from 'src/redux/slices/analyticsReports';
import { formatPracticeComparisonRate } from '../practiceComparisonMetrics';

dayjs.extend(relativeTime);

const PAGE_SIZE = 25;
const EXPORT_PAGE_SIZE = 50;
const EXPORT_CONCURRENCY = 2;
const MAX_EXPORT_ROWS = 5000;
const LEAD_POPUP_FIELDS = '{id,first_name,last_name,contacted_count,created_at,lead_source{type,referral},lead_treatments{treatment,status,custom_price,price}}';

const POPULATION_CONFIG = {
    leads: { label: 'Created Leads', icon: 'eva:person-add-fill', color: '#3b82f6' },
    booked: { label: 'Consultation Booked', icon: 'eva:calendar-fill', color: '#8b5cf6' },
    attended: { label: 'Attended', icon: 'eva:checkmark-circle-2-fill', color: '#10b981' },
    started: { label: 'Treatment Started', icon: 'eva:activity-fill', color: '#f59e0b' },
    appointment_fta: { label: 'Failed to Attend', icon: 'eva:alert-triangle-fill', color: '#ef4444' },
    appointment_resolved: { label: 'Resolved Consultations', icon: 'eva:checkmark-square-2-fill', color: '#64748b' },
};

const isAppointmentPopulation = (population) => population.startsWith('appointment_');

const getOrdering = (population, sortModel) => {
    const appointment = isAppointmentPopulation(population);
    const fieldMap = appointment
        ? {
            name: 'patient_name',
            treatment: 'treatment',
            status: 'status',
            contactedCount: 'contacted_count',
            date: 'datetime',
        }
        : { name: 'first_name', date: 'created_at' };
    const requested = sortModel?.[0] || { field: 'date', sort: 'desc' };
    const field = fieldMap[requested.field] || fieldMap.date;
    return `${requested.sort === 'asc' ? '' : '-'}${field}`;
};

const getName = (item) => (
    item.patient_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown'
);

const getTreatment = (item) => {
    if (item.treatment) return item.treatment;
    const treatments = item.lead_treatments || [];
    return treatments
        .map((entry) => entry?.treatment?.name || entry?.treatment_name || entry?.treatment)
        .filter(Boolean)
        .join(', ') || 'No treatment';
};

const getStatus = (item) => (
    item.status || item.lead_treatments?.[0]?.status || 'NEW'
);

const getSource = (item) => {
    const referral = item?.lead_source?.referral || '';
    const type = item?.lead_source?.type || '';
    return {
        primary: referral || type || '-',
        secondary: referral && type && referral !== type ? type : '',
    };
};

const getValue = (item) => {
    const total = (item.lead_treatments || []).reduce((sum, entry) => {
        const customPrice = Number(entry?.custom_price);
        const price = Number(entry?.price);
        const numericValue = Number.isFinite(customPrice) && customPrice > 0
            ? customPrice
            : price;
        return sum + (Number.isFinite(numericValue) ? numericValue : 0);
    }, 0);
    return total > 0 ? `£${total.toLocaleString()}` : '-';
};

const formatStatus = (status) => String(status || 'Unknown')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const escapeCsv = (value) => {
    const raw = String(value ?? '');
    const spreadsheetSafe = /^[\t\r\n ]*[=+\-@]/.test(raw) ? `'${raw}` : raw;
    return `"${spreadsheetSafe.replace(/"/g, '""')}"`;
};

export const buildPopulationUrl = ({
    practiceId,
    population,
    start,
    end,
    limit,
    offset,
    ordering,
    searchQuery,
}) => {
    const base = `/api/v1/practices/${practiceId}`;
    const searchParam = searchQuery?.trim()
        ? `&search_query=${encodeURIComponent(searchQuery.trim())}`
        : '';
    if (isAppointmentPopulation(population)) {
        return `${base}/reports/lead-status-summary/practice-comparison-drilldown/?population=${population}&start=${start}&end=${end}&limit=${limit}&offset=${offset}&ordering=${ordering}${searchParam}`;
    }
    return `${base}/leads/?practice_comparison_population=${population}&include_archived=true&start=${start}&end=${end}&limit=${limit}&offset=${offset}&ordering=${ordering}&fields=${encodeURIComponent(LEAD_POPUP_FIELDS)}${searchParam}`;
};

const mapResultRow = (item, appointment) => {
    const source = getSource(item);
    return {
        id: item.id,
        leadId: appointment ? item.lead_id : item.id,
        name: getName(item),
        firstName: item.first_name || item.patient_name?.split(' ')[0] || '',
        lastName: item.last_name || item.patient_name?.split(' ').slice(-1)[0] || '',
        treatment: getTreatment(item),
        status: getStatus(item),
        source: source.primary,
        sourceSecondary: source.secondary,
        contactedCount: getReportLeadContactCount(item),
        value: getValue(item),
        date: item.datetime || item.created_at || '',
        dateHuman: item.created_at_human || '',
    };
};

export default function PracticeComparisonRateDialog({ open, onClose, row = null, metric = null }) {
    const buildExportName = useReportExportFilename();
    const [side, setSide] = useState('numerator');
    const [page, setPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 400);
    const [sortModel, setSortModel] = useState([{ field: 'date', sort: 'desc' }]);
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('reportsPopupDateFormat') || 'human_readable');
    const [result, setResult] = useState({ loading: false, error: '', count: 0, rows: [] });
    const [exportState, setExportState] = useState({ loading: false, error: '' });
    const exportGenerationRef = useRef(0);
    const detail = row?.rateDetails?.[metric];
    const selected = detail?.[side];
    const selectedPopulation = selected?.population;
    const populationConfig = POPULATION_CONFIG[selectedPopulation] || {
        label: selected?.label || detail?.label || 'Lead Details',
        icon: 'eva:people-fill',
        color: '#3b82f6',
    };
    const start = row?.practiceComparison?.basis?.start;
    const end = row?.practiceComparison?.basis?.end;

    useEffect(() => {
        if (!open) return;
        setSide('numerator');
        setPage(0);
        setSearchQuery('');
        setExportState({ loading: false, error: '' });
    }, [open, metric, row?.id]);

    useEffect(() => {
        exportGenerationRef.current += 1;
        setExportState({ loading: false, error: '' });
    }, [open, row?.id, metric, side, searchQuery]);

    useEffect(() => {
        if (!open || !selectedPopulation || !row?.id || !start || !end) return undefined;
        let current = true;
        setResult((previous) => ({ ...previous, loading: true, error: '' }));
        const population = selectedPopulation;
        const ordering = getOrdering(population, sortModel);
        const url = buildPopulationUrl({
            practiceId: row.id,
            population,
            start,
            end,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
            ordering,
            searchQuery: debouncedSearchQuery,
        });
        callApi(url).then((response) => {
            if (!current) return;
            if (response?.ok === false || !response?.data) {
                setResult({ loading: false, error: 'Unable to load this population.', count: 0, rows: [] });
                return;
            }
            setResult({
                loading: false,
                error: '',
                count: response.data.count ?? response.data.results?.length ?? 0,
                rows: response.data.results || [],
            });
        }).catch(() => {
            if (current) setResult({ loading: false, error: 'Unable to load this population.', count: 0, rows: [] });
        });
        return () => { current = false; };
    }, [open, selectedPopulation, row?.id, start, end, page, sortModel, debouncedSearchQuery]);

    const rangeLabel = useMemo(() => (
        start && end ? `${dayjs(start).format('DD MMM YYYY')} to ${dayjs(end).format('DD MMM YYYY')}` : ''
    ), [start, end]);

    const exportDateRange = useMemo(() => (
        start && end ? { startDate: dayjs(start).toDate(), endDate: dayjs(end).toDate() } : undefined
    ), [start, end]);

    const formatDate = useCallback((value, humanReadable) => {
        if (!value) return '-';
        const parsed = parseApiDate(value);
        if (!parsed?.isValid()) return '-';
        if (dateFormat === 'human_readable') return humanReadable || parsed.fromNow();
        if (dateFormat === 'date_time_format') return parsed.format('DD MMM YYYY, h:mm A');
        return parsed.format('DD MMM YYYY');
    }, [dateFormat]);

    const tableRows = useMemo(() => {
        const appointment = isAppointmentPopulation(selectedPopulation || '');
        return result.rows.map((item) => mapResultRow(item, appointment));
    }, [result.rows, selectedPopulation]);

    const nameColumn = useMemo(() => ({
        field: 'name',
        headerName: 'Name',
        flex: 1.5,
        minWidth: 180,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 600, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }}>
                    {(params.row.firstName?.[0] || '').toUpperCase()}{(params.row.lastName?.[0] || '').toUpperCase()}
                </Avatar>
                {params.row.leadId ? (
                    <Link
                        href={`/dashboard/practice/${row.id}/lead-detail/${params.row.leadId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontSize: '13px', fontWeight: 500, color: 'primary.main' }}
                    >
                        {params.value}
                    </Link>
                ) : (
                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{params.value}</Typography>
                )}
            </Box>
        ),
    }), [row?.id]);

    const treatmentColumn = useCallback((sortable) => ({
        field: 'treatment',
        headerName: 'Treatment',
        flex: 1.2,
        minWidth: 150,
        sortable,
        renderCell: (params) => <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{params.value}</Typography>,
    }), []);

    const statusColumn = useCallback((appointment) => ({
        field: 'status',
        headerName: appointment ? 'Appointment Status' : 'Status',
        flex: 1.1,
        minWidth: 150,
        sortable: appointment,
        renderCell: (params) => (
            <Chip label={formatStatus(params.value)} size="small" sx={{ fontWeight: 600, fontSize: '11px', height: 24 }} />
        ),
    }), []);

    const dateColumn = useCallback((appointment) => ({
        field: 'date',
        headerName: appointment ? 'Appointment Date' : 'Date',
        flex: 1.1,
        minWidth: 170,
        renderCell: (params) => (
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {formatDate(params.value, params.row.dateHuman)}
            </Typography>
        ),
    }), [formatDate]);

    const contactedCountColumn = useCallback((sortable) => ({
        field: 'contactedCount',
        headerName: 'Times contacted',
        flex: 0.8,
        minWidth: 125,
        align: 'center',
        headerAlign: 'center',
        sortable,
    }), []);

    const columns = useMemo(() => {
        const appointment = isAppointmentPopulation(selectedPopulation || '');
        if (appointment) {
            return [nameColumn, treatmentColumn(true), statusColumn(true), contactedCountColumn(true), dateColumn(true)];
        }
        return [
            nameColumn,
            treatmentColumn(false),
            statusColumn(false),
            {
                field: 'source',
                headerName: 'Source',
                flex: 1,
                minWidth: 130,
                sortable: false,
                renderCell: (params) => (
                    <Box>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{params.value}</Typography>
                        {params.row.sourceSecondary && <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{params.row.sourceSecondary}</Typography>}
                    </Box>
                ),
            },
            contactedCountColumn(false),
            { field: 'value', headerName: 'Value', flex: 0.8, minWidth: 100, align: 'center', headerAlign: 'center', sortable: false },
            dateColumn(false),
        ];
    }, [contactedCountColumn, dateColumn, nameColumn, selectedPopulation, statusColumn, treatmentColumn]);

    const handleExportCSV = useCallback(async () => {
        if (!selectedPopulation || !row?.id || !start || !end || exportState.loading) return;
        const exportGeneration = exportGenerationRef.current + 1;
        exportGenerationRef.current = exportGeneration;
        const isCurrentExport = () => exportGenerationRef.current === exportGeneration;
        setExportState({ loading: true, error: '' });
        try {
            const appointment = isAppointmentPopulation(selectedPopulation);
            const ordering = getOrdering(selectedPopulation, sortModel);
            const makeUrl = (offset) => buildPopulationUrl({
                practiceId: row.id,
                population: selectedPopulation,
                start,
                end,
                limit: EXPORT_PAGE_SIZE,
                offset,
                ordering,
                searchQuery: debouncedSearchQuery,
            });
            const firstResponse = await callApi(makeUrl(0));
            if (!isCurrentExport()) return;
            if (firstResponse?.ok === false || !firstResponse?.data) throw new Error('Unable to export this population.');
            const firstRows = firstResponse.data.results || [];
            const totalCount = firstResponse.data.count ?? firstRows.length;
            if (totalCount > MAX_EXPORT_ROWS) {
                throw new Error('Export is limited to 5,000 records. Refine your search and try again.');
            }
            let allRows = [...firstRows];
            const remainingOffsets = [];
            const pageStep = firstRows.length || EXPORT_PAGE_SIZE;
            for (let offset = pageStep; offset < totalCount; offset += pageStep) {
                remainingOffsets.push(offset);
            }
            for (let index = 0; index < remainingOffsets.length; index += EXPORT_CONCURRENCY) {
                const batch = remainingOffsets.slice(index, index + EXPORT_CONCURRENCY);
                // eslint-disable-next-line no-await-in-loop
                const responses = await Promise.all(batch.map((offset) => callApi(makeUrl(offset))));
                if (!isCurrentExport()) return;
                if (responses.some((response) => response?.ok === false || !response?.data)) {
                    throw new Error('Unable to export this population.');
                }
                allRows = allRows.concat(responses.flatMap((response) => response.data.results || []));
            }
            const exportRows = allRows.map((item) => mapResultRow(item, appointment));
            if (!exportRows.length) throw new Error('No matching records to export.');
            const headers = appointment
            ? ['Name', 'Treatment', 'Appointment Status', 'Times contacted', 'Appointment Date']
            : ['Name', 'Treatment', 'Status', 'Source', 'Times contacted', 'Value', 'Date'];
            const csvRows = exportRows.map((item) => (appointment
            ? [
                item.name,
                item.treatment,
                formatStatus(item.status),
                item.contactedCount,
                formatDate(item.date, item.dateHuman),
            ]
            : [
                item.name,
                item.treatment,
                formatStatus(item.status),
                item.sourceSecondary ? `${item.source} (${item.sourceSecondary})` : item.source,
                item.contactedCount,
                item.value,
                formatDate(item.date, item.dateHuman),
            ]));
            const csvContent = [headers, ...csvRows]
                .map((csvRow) => csvRow.map(escapeCsv).join(','))
                .join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            if (!isCurrentExport()) return;
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = buildExportName(
                `practice-comparison-${selectedPopulation || 'population'}`,
                exportDateRange,
                { practiceName: row?.practiceName }
            );
            link.click();
            URL.revokeObjectURL(link.href);
            if (isCurrentExport()) setExportState({ loading: false, error: '' });
        } catch (error) {
            if (isCurrentExport()) {
                setExportState({ loading: false, error: error?.message || 'Unable to export this population.' });
            }
        }
    }, [buildExportName, debouncedSearchQuery, end, exportDateRange, exportState.loading, formatDate, row?.id, row?.practiceName, selectedPopulation, sortModel, start]);

    const handleClose = () => {
        exportGenerationRef.current += 1;
        setPage(0);
        setSearchQuery('');
        onClose();
    };

    if (!detail) return null;

    return (
        <Dialog
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    height: 'auto',
                    maxHeight: '85vh',
                },
            }}
        >
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(populationConfig.color, 0.1),
                            }}
                        >
                            <Iconify
                                icon={populationConfig.icon}
                                width={22}
                                height={22}
                                sx={{ color: populationConfig.color }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
                                {row.practiceName} — {populationConfig.label}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                <Chip
                                    label={`${detail.label}: ${formatPracticeComparisonRate(detail.value)}`}
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(populationConfig.color, 0.1),
                                        color: populationConfig.color,
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        height: 22,
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                                    {rangeLabel}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Export CSV" arrow>
                            <span>
                                <IconButton
                                    aria-label="Export CSV"
                                    size="small"
                                    onClick={handleExportCSV}
                                    disabled={!tableRows.length || result.loading || exportState.loading}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        border: '1px solid',
                                        borderColor: '#e2e8f0',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                    }}
                                >
                                    <Iconify icon="eva:download-outline" width={18} sx={{ color: '#64748b' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button variant="outlined" size="small" onClick={handleClose} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ p: 2.5, overflowY: 'auto' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h4">{formatPracticeComparisonRate(detail.value)}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{detail.formula}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {metric === 'ftaRate' ? 'Consultation appointment outcomes' : 'Lead cohort: enquiries created'} {rangeLabel}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button variant={side === 'numerator' ? 'contained' : 'outlined'} onClick={() => { exportGenerationRef.current += 1; setSide('numerator'); setPage(0); }}>
                            {detail.numerator.label}: {detail.numerator.count}
                        </Button>
                        <Button variant={side === 'denominator' ? 'contained' : 'outlined'} onClick={() => { exportGenerationRef.current += 1; setSide('denominator'); setPage(0); }}>
                            {detail.denominator.label}: {detail.denominator.count}
                        </Button>
                    </Stack>
                </Stack>

                {metric === 'ftaRate' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Cancelled and still-booked appointments are excluded from the denominator.
                    </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Search records"
                        value={searchQuery}
                        onChange={(event) => {
                            setSearchQuery(event.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" width={18} /></InputAdornment>,
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Date display"
                        value={dateFormat}
                        onChange={(event) => {
                            setDateFormat(event.target.value);
                            localStorage.setItem('reportsPopupDateFormat', event.target.value);
                        }}
                        sx={{ minWidth: 190 }}
                    >
                        <MenuItem value="human_readable">Relative time</MenuItem>
                        <MenuItem value="date_format">Date</MenuItem>
                        <MenuItem value="date_time_format">Date and time</MenuItem>
                    </TextField>
                </Stack>
                {result.loading && result.rows.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>}
                {result.error && <Alert severity="error">{result.error}</Alert>}
                {exportState.error && <Alert severity="error" sx={{ mb: 2 }}>{exportState.error}</Alert>}
                {!result.loading && !result.error && result.rows.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography color="text.secondary">No matching records.</Typography>
                    </Box>
                )}
                {!result.error && result.rows.length > 0 && (
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <DataGridPro
                                autoHeight
                                rows={tableRows}
                                columns={columns}
                                loading={result.loading}
                                rowCount={result.count}
                                pagination
                                paginationMode="server"
                                paginationModel={{ page, pageSize: PAGE_SIZE }}
                                onPaginationModelChange={(model) => setPage(model.page)}
                                pageSizeOptions={[PAGE_SIZE]}
                                sortingMode="server"
                                sortModel={sortModel}
                                onSortModelChange={(model) => {
                                    setSortModel(model.length ? [model[0]] : [{ field: 'date', sort: 'desc' }]);
                                    setPage(0);
                                }}
                                disableRowSelectionOnClick
                                disableVirtualization
                                sx={{
                                    minWidth: isAppointmentPopulation(selectedPopulation || '') ? 720 : 1050,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '12px',
                                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
                                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
                                    '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9' },
                                    '& .MuiDataGrid-row:hover': { bgcolor: '#f8fafc' },
                                }}
                            />
                        </Box>
                )}
            </Box>
        </Dialog>
    );
}

PracticeComparisonRateDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    row: PropTypes.object,
    metric: PropTypes.string,
};
